<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isEmpty } from 'lodash';
import { useLogStore } from '@/store/LogStore';
import { useToast } from 'primevue/usetoast';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputTextAera from 'primevue/textarea';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ProofPricingModal from '@/components/modal/ProofPricingModal.vue';
import QuoteHistoryModal from '@/components/modal/QuoteHistoryModal.vue';

const { t } = useI18n({ useScope: 'global' });
const quoteId = ref('');
const dialog = useDialog();
const logs = ref([]);
const hideShow = ref({});
const toast = useToast();
const logStore = useLogStore();
const isLoading = ref(false);

const getXMLs = () => {
  if (!quoteId.value) {
    toast.add({ severity: 'error', detail: t('validate.required'), life: 5000 });
    return;
  }
  const payload = {
    quoteId: quoteId.value
  };
  isLoading.value = true;
  logStore
    .handleGetXml(payload)
    .then((res) => {
      logs.value = res.data;
      if (isEmpty(res.data)) {
        toast.add({ severity: 'error', detail: t('common.nodata'), life: 5000 });
      }
    })
    .catch((error) => {
      toast.add({ severity: 'error', detail: error.data, life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const expandPricingFunc = (type) => {
  hideShow.value[type] = !hideShow.value[type];
};

const download = (filename, data) => {
  const blob = new Blob([data], { type: 'text/plain;charset=UTF-8' });
  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveBlob(blob, filename);
  } else {
    const elem = window.document.createElement('a');
    elem.href = window.URL.createObjectURL(blob);
    elem.download = filename;
    document.body.appendChild(elem);
    elem.click();
    document.body.removeChild(elem);
  }
};

const openModalViewPricing = (data) => {
  dialog.open(ProofPricingModal, {
    data: {
      pricingData: data?.proofOfPricing
    },
    props: {
      header: t('proof_pricing.group_quote_pricing') + ' ' + data.quoteId,
      modal: true,
      maximizable: true
    }
  });
};

const openModalHistory = (logHistory: any) => {
  const dialogRef = dialog.open(QuoteHistoryModal, {
    data: {
      historyData: logHistory.worksheetHistoryLog,
      quoteId: quoteId.value
    },
    props: {
      header: t('menu.quote_history') + ' - ' + logHistory.quoteId,
      modal: true,
      style: {
        width: '75vw'
      }
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const getOption = (option: number) => {
  return option ? ' - ' + t('common.option') + ' ' + option : '';
};

const sortedArray = computed(() => {
  return [...logs.value].sort((a, b) => {
    const getHotelPriority = (name: string) => {
      if (name === 'Amadeus') {
        return 1;
      }
      if (name === 'HOTELEX') {
        return 2;
      }
      if (name === 'Quote History') {
        return 3;
      }
      return 0;
    };

    const priorityA = getHotelPriority(a.hotelName);
    const priorityB = getHotelPriority(b.hotelName);
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    const optionA = a.option ? parseInt(a.option) : 10;
    const optionB = b.option ? parseInt(b.option) : 10;
    if (optionA !== optionB) {
      return optionA - optionB;
    }

    const dateA = new Date(a.creationTime).getTime();
    const dateB = new Date(b.creationTime).getTime();
    return dateA - dateB;
  });
});
</script>
<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="card">
    <div class="logs">
      <div class="grid flex align-items-center gap-4 mb-6">
        <div class="font-bold text-4xl">{{ $t('common.quote') }} #</div>
        <div class="col-2">
          <InputText type="text" id="quoteId" v-model="quoteId" @keyup.enter="getXMLs" />
        </div>
        <Button @click="getXMLs" id="submit-btn" class="col-1" :label="$t('common.buttons.submit')"></Button>
      </div>
      <div v-for="(log, index) in sortedArray" :key="index">
        <div v-if="log.hotelName === 'Quote History'" @click="openModalHistory(log)" class="log-panels cursor-pointer quote-history w-8 font-bold text-xl bg-yellow-500 p-3 my-3 flex align-items-center justify-content-between">
          <div>
            {{ $t('menu.quote_history') }}
            <span class="cursor-pointer"> [<i class="pi pi-plus"></i>]</span>
          </div>
        </div>
        <div v-else>
          <div class="log-panels w-8 font-bold text-xl bg-yellow-500 p-3 my-3 flex align-items-center justify-content-between">
            <div>
              {{ log.hotelName + getOption(log.option) }} ({{ log.creationTime }})&nbsp;&nbsp;
              <span class="cursor-pointer expand-log" @click="expandPricingFunc(index)"> [<i class="pi pi-minus" v-if="hideShow[index]"></i> <i class="pi pi-plus" v-else></i>] </span>
            </div>
            <div>
              <Button v-if="log?.proofOfPricing?.length > 0" size="small" severity="danger" class="custom-btn" :label="$t('proof_pricing.show_price_proof')" @click="openModalViewPricing(log)" />
            </div>
          </div>
          <div class="flex flex-row gap-5" v-if="hideShow[index]">
            <div class="flex flex-col w-6">
              <div class="flex justify-content-between">
                <div class="font-bold text-base">{{ $t('management.log.request_xml') }}</div>
                <div class="cursor-pointer pr-2 pb-2 text-2xl pi pi-download" @click="download('Request', log.request)"></div>
              </div>
              <InputTextAera class="w-full text-area-xml" v-model="log.request" rows="18" />
            </div>
            <div class="flex flex-col w-6">
              <div class="flex justify-content-between">
                <div class="font-bold text-base">{{ $t('management.log.response_xml') }}</div>
                <div class="cursor-pointer pr-2 pb-2 text-2xl pi pi-download" @click="download('Request', log.response)"></div>
              </div>
              <InputTextAera class="w-full text-area-xml" v-model="log.response" rows="18" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
